import { HSDFormatType } from "@app/exports/enumerations";
import { Features } from "@app/feature-flags";
import { NetworkAssessmentAnalysisType, NextClosestProviderAction, PatientVolumeLabelEnum, ProviderNetworkType, RegionSetStatusType } from "@app/projects/enumerators";
import { QESRadioButton, QESRadioGroup } from "@app/shared/qes-standards";
import { Roles } from "@app/user";
import { isEqual, startCase } from "lodash";
import * as React from "react";
import { ProjectModule } from "../../projects/literals";
import { ExportFormatType, ExportGapFilterType, ExportProviderAvailabilityType, NetworkProviderComparisonType, NetworkProviderFilterType, ProviderDropdownTypes, ReportSettingsType } from "../enumerations";
import { ExportAdequacyServicingProvidersModel, ExportAttestationServicingProvidersModel, ExportCompositeServicingProvidersModel, RegionSetFilter, ReportSettingsModel, SpecialtyFilter } from "../exportModels";
import ExportService from "../exportService";
import * as helpers from "./exportPanelBodyHelpers";
import { SelectFormat, SelectProviderFilter, SelectRegionSetFilter, SelectSpecialtyFilter } from "./filters";
import GenerateBody from "./generate-body";
import { ReportSettingsBody, ReportSettingsSaveAction } from "./report-settings";
import { ReportSettingsProps, ReportSettingsState } from "./report-settings/reportSettings";
import { ReportPanelBodyBaseProps, ReportPanelBodyBaseState } from "./reportPanelBodyBase";
import * as exportDataToast from "./toast";
import { toast } from "react-toastify";

interface ServicingProvidersPanelBodyProps extends ReportPanelBodyBaseProps, ReportSettingsProps {
  activeModule: ProjectModule;
  activeNetworkType: ProviderNetworkType;
  defaultAppendPatternOfCareProviders: boolean;
  defaultHsdFormatType: HSDFormatType;
  excelRecordsLimit: number;
  exportNetworkProviderCount: number;
  inspectionUids: string[];
  networkTypes: ProviderNetworkType[];
  providerAvailabilityType: ExportProviderAvailabilityType;
  providerIds: number[];
  specialtyGroupCodes: string[];
  showAppendNextClosestProviders: boolean;
  showCountyFirst: boolean;
  showNetNewServicingProviders: boolean;
  showNextClosestProvider: boolean;
  showAppendPatternOfCareProviders: boolean;
}

interface ServicingProvidersPanelBodyState extends ReportPanelBodyBaseState, ReportSettingsState {
  appendNextClosestProviders: boolean;
  appendPatternOfCareProviders: boolean;
  hsdFormat: HSDFormatType;
  networkProviderFilterType: NetworkProviderFilterType;
  providerComparisonTypes: NetworkProviderComparisonType[];
  providerFilter: {
    filterType: ProviderNetworkType;
    providerAvailabilityType: ExportProviderAvailabilityType;
  };
  regionSetFilter: RegionSetFilter;
  sharedUsers: number[];
  specialtyFilter: SpecialtyFilter;
}

export default class ServicingProvidersPanelBody extends React.Component<ServicingProvidersPanelBodyProps, ServicingProvidersPanelBodyState> {
  private readonly exportService = new ExportService();
  private readonly testIdSuffix = "servicing-providers-panel";
  /*hsdFormat = HSDFormatType.Default;*/

  private hsdFormatFilters = [
    { key: HSDFormatType.Default, value: "Default Format" },
    { key: HSDFormatType.MA, value: "MA HSD Format" },
    { key: HSDFormatType.MMP, value: "MMP HSD Format" },
    { key: HSDFormatType.CCIIO_PPF_REPORT, value: "CCIIO PPF Report" },
    { key: HSDFormatType.DISTINCT_SERVICING_PROVIDER_REPORT, value: "Distinct Servicing Providers" },
  ];

  constructor(props: ServicingProvidersPanelBodyProps) {
    super(props);

    this.state = {
      ...this.getInitialBodyState(),
      ...this.getInitialReportSettingsState()
    };
  }

  public componentDidUpdate(prevProps: ServicingProvidersPanelBodyProps) {
    if (!isEqual(prevProps.reportSettingsList, this.props.reportSettingsList)) {
      const reportSettingsList = this.props.reportSettingsList;
      if (reportSettingsList) {
        const currentReportSettings = reportSettingsList.find(rs => rs.isDefault);
        if (currentReportSettings)
          this.handleParseReportSettingsJsonToState(currentReportSettings.reportSettingsJson);
        this.setState({
          currentReportSettings,
          reportSettingsList
        })
      }
    }
  }

  public componentWillUnmount() {
    exportDataToast.dismiss();
  }

  public render() {
    const isAccuracyModule = this.props.match.params.module === "accuracy";

    return (
      <>
        {isAccuracyModule &&
          <ReportSettingsBody
            buildReportSettingsModel={this.buildReportSettingsModel}
            createReportSettingsNameInputDisabled={this.state.createReportSettingsNameInputDisabled}
            currentReportSettings={this.state.currentReportSettings}
            isSelectionValid={this.state.isSelectionValid}
            parseReportSettingsJsonToState={this.handleParseReportSettingsJsonToState}
            reportSettingsHasChanges={this.state.reportSettingsHasChanges}
            reportSettingsHasInvalidChanges={this.state.reportSettingsHasInvalidChanges}
            reportSettingsList={this.state.reportSettingsList}
            saveClicked={this.state.saveClicked}
            setCreateReportSettingsNameInputDisabled={this.setCreateReportSettingsNameInputDisabled}
            setCurrentReportSettings={this.setCurrentReportSettings}
            setReportSettingsIsSelectionValid={this.setReportSettingsIsSelectionValid}
            setReportSettingsHasChanges={this.setReportSettingsHasChanges}
            setReportSettingsHasInvalidChanges={this.setReportSettingsHasInvalidChanges}
            setReportSettingsList={this.setReportSettingsList}
            setSaveClicked={this.setSaveClicked}
          />
        }
        <div className="row">
          <div className="col-12 col-sm-3">
            <SelectProviderFilter
              {...this.props}
              {...this.state}
              onAppendNextClosestProvidersChange={this.handleAppendNextClosestProvidersChange}
              onProviderComparisonTypeChange={this.handleProviderComparisonTypeChange}
              onNetworkProviderFilterTypeChange={this.handleNetworkProviderFilterTypeChange}
              onProviderFilterTypeChange={this.handleProviderFilterTypeChange}
              onProviderAvailabilityTypeChange={this.handleProviderAvailabilityTypeChange}
              showChangedProvidersSelection={this.props.comparedRunConfigurationId !== null}
              showPatternOfCareProviders={this.props.showAppendPatternOfCareProviders}
              onPatternOfCareProvidersChange={this.handlePatternOfCareProvidersChange}
              isFormatHsd={this.state.hsdFormat === HSDFormatType.MA || this.state.hsdFormat === HSDFormatType.MMP}
            />
          </div>
          {this.props.showCountyFirst ? (<React.Fragment>{this.renderSelectRegionFilter()}{this.renderSelectSpecialtyFilter()}</React.Fragment>) :
            (<React.Fragment>{this.renderSelectSpecialtyFilter()}{this.renderSelectRegionFilter()}</React.Fragment>)}

          <div className="col-12 col-sm-3">
            <SelectFormat
              excelDisabled={this.getExportProviderCount(this.state.providerFilter.filterType, this.state.networkProviderFilterType) > this.props.excelRecordsLimit}
              exportType="servicing-providers"
              outputFormat={this.state.outputFormat}
              onOutputFormatChange={this.handleOutputFormatChange} />
            {this.getExportProviderCount(this.state.providerFilter.filterType, this.state.networkProviderFilterType) > this.props.excelRecordsLimit && <p className="text-danger"><i>Results are too large for Microsoft Excel Workbook (.xlsx).</i></p>}
            {this.renderHsdRadio()}
          </div>
        </div>
        {isAccuracyModule &&
          <ReportSettingsSaveAction
            currentReportSettings={this.state.currentReportSettings}
            isSelectionValid={this.state.isSelectionValid}
            reportSettingsHasChanges={this.state.reportSettingsHasChanges}
            reportSettingsHasInvalidChanges={this.state.reportSettingsHasInvalidChanges}
            reportSettingsList={this.state.reportSettingsList}
            saveClicked={this.state.saveClicked}
            setCurrentReportSettings={this.setCurrentReportSettings}
            setCreateReportSettingsNameInputDisabled={this.setCreateReportSettingsNameInputDisabled}
            setReportSettingsHasChanges={this.setReportSettingsHasChanges}
            setReportSettingsList={this.setReportSettingsList}
            setSaveClicked={this.setSaveClicked}
            stringifyStateReportSettings={this.stringifyStateReportSettings}
          />
        }
        <GenerateBody
          {...this.props}
          emailOnSuccess={this.state.emailOnSuccess}
          reportName={this.state.reportName}
          onGenerateClick={this.handleGenerateClick}
          onEmailOnSuccessChange={this.handleEmailOnSuccessChange}
          onReportNameChange={this.handleReportNameChange}
          isLoading={this.state.loadingReportSettingsJSONStrings && this.state.loadingReportSettingsJSONStrings.indexOf(helpers.stringifyStateReportSettings(this.state)) > -1}
          isValid={(this.state.regionSetFilter.includeActive || this.state.regionSetFilter.includePending || this.state.regionSetFilter.includeApproved)}
          showSharedUsers={this.props.match.params.module === "adequacy"}
          sharedUsers={this.state.sharedUsers}
          updateSelectedSharedUsers={this.updateSharedUsers}
          exportType="AdequacyServicingProvider"
          projectId={this.props.projectId}
          networkAssessmentAnalysisType={this.state.regionSetFilter.networkAssessmentAnalysisType}
          networkProviderFilterType={this.state.networkProviderFilterType}
        />
      </>
    );
  }

  private getDefaultProviderAvailabilityType = (updatedNetworkType?: ProviderNetworkType): ExportProviderAvailabilityType => {
    if (this.props.comparedRunConfigurationId !== null) {
      return ExportProviderAvailabilityType.Changed;
    }

    const networkType = updatedNetworkType !== null
      ? updatedNetworkType
      : this.props.activeNetworkType;
    if (networkType === ProviderNetworkType.NetNew &&
      this.props.providerAvailabilityType !== ExportProviderAvailabilityType.Targeted) {
      // While not in the Targeted view, default net new provider availability to All.
      return ExportProviderAvailabilityType.All;
    }
    return this.props.providerAvailabilityType;
  }

  private getInitialBodyState = (updatedNetworkType?: ProviderNetworkType): ServicingProvidersPanelBodyState => {
    return {
      appendNextClosestProviders: false,
      appendPatternOfCareProviders: this.props.defaultAppendPatternOfCareProviders,
      emailOnSuccess: true,
      hsdFormat: this.props.enabledFeatureFlags.has(Features.DISTINCT_SERVICING_PROVIDER_REPORT) && this.props.user.roles.includes(Roles.DISTINCT_SERVICING_USER) && !this.props.defaultAppendPatternOfCareProviders
        ? HSDFormatType.DISTINCT_SERVICING_PROVIDER_REPORT
        : !!this.props.defaultHsdFormatType
          ? this.props.defaultHsdFormatType
          : HSDFormatType.Default,
      loadingReportSettingsJSONStrings: [],
      networkProviderFilterType: NetworkProviderFilterType.Available,
      outputFormat: ExportFormatType.CSV,
      providerComparisonTypes: [NetworkProviderComparisonType.Added, NetworkProviderComparisonType.Matched, NetworkProviderComparisonType.Removed],
      providerFilter: {
        filterType: this.props.activeNetworkType || ProviderNetworkType.Network,
        providerAvailabilityType: this.getDefaultProviderAvailabilityType(updatedNetworkType),
      },
      regionSetFilter: {
        filterType: ExportGapFilterType.All,
        includeActive: true,
        includeApproved: true,
        includePending: true,
        networkAssessmentAnalysisType: this.props.initialNetworkAssessmentAnalysisType,
      },
      reportName: helpers.reportName(this.props.comparedRunConfigurationId !== null ? "ChangedProviders" : startCase(this.props.activeNetworkType || ProviderNetworkType.Network)),
      sharedUsers: [],
      specialtyFilter: {
        filterType: ExportGapFilterType.All,
        includeFacilitySpecialties: true,
        includePhysicianSpecialties: true,
      },
    } as ServicingProvidersPanelBodyState;
  }

  private getInitialReportSettingsState = (): ReportSettingsState => {
    return {
      createReportSettingsNameInputDisabled: true,
      currentReportSettings: undefined,
      isSelectionValid: true,
      reportSettingsHasChanges: false,
      reportSettingsHasInvalidChanges: false,
      reportSettingsList: [],
      saveClicked: false,
    } as ReportSettingsState;
  }


  private renderSelectRegionFilter() {
    return (
      <div className="col-12 col-sm-3">
        <SelectRegionSetFilter
          disabled={this.props.disableRegionFilters}
          onNetworkAssessmentAnalysisTypeChange={this.handleNetworkAssessmentAnalysisTypeChange}
          onRegionSetFilterTypeChange={this.handleRegionSetFilterTypeChange}
          onRegionSetIncludeChange={this.handleRegionSetIncludeChange}
          regionSetFilter={this.state.regionSetFilter}
          singularDisplay={this.props.singularDisplay}
          pluralDisplay={this.props.pluralDisplay}
          testIdSuffix={this.testIdSuffix}
        />
      </div>
    );
  }

  private renderSelectSpecialtyFilter() {
    return (
      <div className="col-12 col-sm-3">
        <SelectSpecialtyFilter
          adequacyAnalysisType="county"
          disabled={this.props.disableSpecialtyGroupFilters}
          onSpecialtyGapFilterChange={this.handleSpecialtyGapFilterChange}
          onSpecialtyIncludeChange={this.handleSpecialtyIncludeChange}
          onExpandableSpecialtyIncludeChange={() => void false}
          showExpandableSpecialtyOption={false}
          specialtyFilter={this.state.specialtyFilter}
          testIdSuffix={this.testIdSuffix}
        />
      </div>
    );
  }

  private renderHsdRadio() {
    return (
      <div className="form-group row qes-radio-group__container">
        <p className="col-12 col-form-label ptb0 text-left mb-1">Filter</p>
        <QESRadioGroup groupContainerClass="ml-3">
          {this.getFormatFilters().map(({ key, value }) => {
            return (
              <QESRadioButton
                key={key}
                label={value}
                className="qes-radio mt-0"
                value={key}
                checked={this.state.hsdFormat === key}
                onChange={(e) => e.target.checked ? this.handleProviderFilterIsHsdChange(key) : {}}
                disabled={!(this.state.outputFormat === "csv" || this.state.outputFormat === "tab")}
                testId={`servicing-providers-panel-body__${key}`}
                name="servicing-providers-panel-hsd"
              />
            );
          })}
        </QESRadioGroup>
      </div>
    );
  }

  private getFormatFilters() {
    let formatFilters = this.hsdFormatFilters.slice();
    if (!this.props.user.roles.includes(Roles.CCIIO_PPF_REPORT_USER)) {
      formatFilters = formatFilters.filter(ff => ff.key !== HSDFormatType.CCIIO_PPF_REPORT);
    }

    if (!this.props.enabledFeatureFlags.has(Features.DISTINCT_SERVICING_PROVIDER_REPORT) || !this.props.user.roles.includes(Roles.DISTINCT_SERVICING_USER)) {
      formatFilters = formatFilters.filter(ff => ff.key !== HSDFormatType.DISTINCT_SERVICING_PROVIDER_REPORT);
    }
    return formatFilters;
  }

  private getExportProviderCount(networkType: ProviderNetworkType, networkProviderFilterType: NetworkProviderFilterType): number {
    switch (networkType) {
      case ProviderNetworkType.Network:
        if (networkProviderFilterType === NetworkProviderFilterType.Terminated
          || networkProviderFilterType === NetworkProviderFilterType.Unknown) {
          return 0;
        }
        return this.props.exportNetworkProviderCount;
      default:
        return 0;
    }
  }

  private handleProviderFilterIsHsdChange = (hsdFormat: HSDFormatType) => {
    const { callback, newAppendPatternOfCare } = this.updatePatternOfCareCheckbox(this.state.providerFilter.filterType, hsdFormat);
    this.setState({
      appendPatternOfCareProviders: newAppendPatternOfCare,
      hsdFormat,
      reportSettingsHasChanges: true,
    }, callback);
  }

  private handleNetworkProviderFilterTypeChange = (networkProviderFilterType: NetworkProviderFilterType) => {
    if (networkProviderFilterType === NetworkProviderFilterType.Available && this.state.hsdFormat == HSDFormatType.Default) {
      const hsdFormat = HSDFormatType.Default;

      this.setState({ hsdFormat, networkProviderFilterType, reportSettingsHasChanges: true, });
    } else {
      this.setState({ networkProviderFilterType, reportSettingsHasChanges: true, });
    }
  }

  private handleAppendNextClosestProvidersChange = (appendNextClosest: boolean) => {
    this.setState({ appendNextClosestProviders: appendNextClosest, reportSettingsHasChanges: true, });
  }

  private handlePatternOfCareProvidersChange = (appendPatternOfCare: boolean) => {
    this.setState({ appendPatternOfCareProviders: appendPatternOfCare, reportSettingsHasChanges: true, });
  }

  private handleProviderComparisonTypeChange = (providerComparisonType: NetworkProviderComparisonType) => {
    const newComparisonTypes = [...this.state.providerComparisonTypes];

    const currentIndex = newComparisonTypes.indexOf(providerComparisonType);
    if (currentIndex >= 0) {
      newComparisonTypes.splice(currentIndex, 1);
    } else {
      newComparisonTypes.push(providerComparisonType);
    }

    this.setState({ providerComparisonTypes: newComparisonTypes, reportSettingsHasChanges: true, });
  }

  private handleProviderFilterTypeChange = (networkType: ProviderNetworkType) => {
    const newName = helpers.reportName(startCase(networkType));
    // Pass the updated network type during a provider filter type change
    // to ensure the provider availability is correctly defaulted.
    const initialBodyState = this.getInitialBodyState(networkType);

    const { callback, newAppendPatternOfCare } = this.updatePatternOfCareCheckbox(networkType, this.state.hsdFormat);

    this.setState({
      ...initialBodyState,
      appendPatternOfCareProviders: newAppendPatternOfCare,
      providerFilter: {
        ...initialBodyState.providerFilter,
        filterType: networkType,
      },
      reportName: newName,
      reportSettingsHasChanges: true,
    }, callback);
  }

  private handleProviderAvailabilityTypeChange = (providerAvailabilityType: ExportProviderAvailabilityType) => {
    this.setState((prevState) => ({
      ...prevState,
      providerFilter: {
        ...prevState.providerFilter,
        providerAvailabilityType,
      },
      reportSettingsHasChanges: true,
    }));
  }

  private handleOutputFormatChange = (outputFormat: ExportFormatType) => {
    if (!(outputFormat === "csv" || outputFormat === "tab")) {
      const hsdFormat = HSDFormatType.Default;
      this.setState({ hsdFormat });
    }
    this.setState({ outputFormat, reportSettingsHasChanges: true, });
  }

  private handleRegionSetFilterTypeChange = (filterType: ExportGapFilterType) => {
    this.setState((prevState) => ({
      ...prevState,
      regionSetFilter: {
        ...prevState.regionSetFilter,
        filterType,
      },
      reportSettingsHasChanges: true,
    }));
  }

  private handleNetworkAssessmentAnalysisTypeChange = (networkAssessmentAnalysisType: NetworkAssessmentAnalysisType) => {
    this.setState((prevState) => ({
      ...prevState,
      regionSetFilter: {
        ...prevState.regionSetFilter,
        networkAssessmentAnalysisType,
      },
      reportSettingsHasChanges: true,
    }));
  }

  private handleRegionSetIncludeChange = (selected: boolean, regionSetStatus: RegionSetStatusType) => {
    let activeSelected: boolean = this.state.regionSetFilter.includeActive;
    let approvedSelected: boolean = this.state.regionSetFilter.includeApproved;
    let pendingSelected: boolean = this.state.regionSetFilter.includePending;

    switch (regionSetStatus) {
      case RegionSetStatusType.Active:
        activeSelected = selected;
        break;
      case RegionSetStatusType.Approved:
        approvedSelected = selected;
        break;
      case RegionSetStatusType.Pending:
        pendingSelected = selected;
        break;
    }

    this.setState((prevState) => ({
      ...prevState,
      regionSetFilter: {
        ...prevState.regionSetFilter,
        includeActive: activeSelected,
        includeApproved: approvedSelected,
        includePending: pendingSelected,
      },
      reportSettingsHasChanges: true,
    }));
  }

  // if the provider network type changes to not network 
  // or the report format changes to not MA or MMP
  // uncheck pattern of care and notify the user
  private updatePatternOfCareCheckbox = (newProviderType: ProviderNetworkType, newFormatType: HSDFormatType) => {
    let newAppendPatternOfCare = this.state.appendPatternOfCareProviders;
    let callback = () => { };
    if (newAppendPatternOfCare === true
      && (newProviderType !== ProviderNetworkType.Network
        || (newFormatType !== HSDFormatType.MA && newFormatType !== HSDFormatType.MMP))) {
      newAppendPatternOfCare = false;

      callback = () => {
        if (this.props.user.roles.includes(Roles.PATTERN_OF_CARE_USER)
          && this.props.enabledFeatureFlags.has(Features.MANAGE_HSD_PROVIDERS)
          && this.props.showAppendPatternOfCareProviders) {
          toast("\"Append Pattern of Care Providers\" was unchecked", {
            containerId: "z-index-1030",
            type: "info",
          });
        }
      };
    }
    return { callback, newAppendPatternOfCare };
  }

  private handleGenerateClick = async () => {
    const stateReportSettingsJSON = helpers.stringifyStateReportSettings(this.state);
    this.setState((prevState) => ({
      ...prevState,
      loadingReportSettingsJSONStrings: [
        ...prevState.loadingReportSettingsJSONStrings,
        stateReportSettingsJSON,
      ],
    }));

    switch (this.props.match.params.module) {
      case "adequacy":
        await this.generateExportAdequacyServicingProvidersModel();
        break;
      case "attestation":
        await this.generateExportAttestationServicingProvidersModel();
        break;
      case "composite":
        await this.generateExportCompositeServicingProvidersModel();
        break;
      default:
        break;
    }

    exportDataToast.toastInfo(this.state.reportName);
  }

  // Adequacy
  private generateExportAdequacyServicingProvidersModel = async () => {
    const servicingProviderModel: ExportAdequacyServicingProvidersModel = {
      appendPatternOfCareProviders: this.state.appendPatternOfCareProviders,
      comparedProjectRunConfigurationId: this.state.providerFilter.providerAvailabilityType === ExportProviderAvailabilityType.Changed ? this.props.comparedRunConfigurationId : null,
      customProjectId: this.props.projectId,
      eMailOnSuccess: this.state.emailOnSuccess,
      exportName: this.state.reportName,
      formatType: this.state.outputFormat,
      hsdFormat: this.state.hsdFormat,
      impactProviderType: this.state.providerFilter.providerAvailabilityType,
      includeActiveRegionSets: this.state.regionSetFilter.includeActive,
      includeApprovedRegionSets: this.state.regionSetFilter.includeApproved,
      includeFacilitySpecialties: this.state.specialtyFilter.includeFacilitySpecialties,
      includePendingRegionSets: this.state.regionSetFilter.includePending,
      includePhysicianSpecialties: this.state.specialtyFilter.includePhysicianSpecialties,
      isZipAnalysis: false,
      networkAssessmentAnalysisTypes: [this.state.regionSetFilter.networkAssessmentAnalysisType],
      networkProviderFilterType: this.state.networkProviderFilterType,
      nextClosestProviderAction: this.state.providerFilter.filterType.toString() === ProviderDropdownTypes.NextClosestProviders ? NextClosestProviderAction.FilterToNextClosestProvidersOnly : this.state.appendNextClosestProviders ? NextClosestProviderAction.AppendNextClosestProviders : NextClosestProviderAction.None,
      projectRunConfigurationId: this.props.runConfigurationId,
      projectURL: this.props.projectURL,
      providerClaimsVolumeFilter: PatientVolumeLabelEnum.Unknown,
      providerComparisonTypes: this.state.providerComparisonTypes,
      providerIds: this.props.providerIds,
      providerNetworkType: this.state.providerFilter.filterType.toString() === ProviderDropdownTypes.NextClosestProviders ? ProviderNetworkType.Network : this.state.providerFilter.filterType,
      regionSetGapFilter: this.state.regionSetFilter.filterType,
      regionSetIds: this.props.regionSetIds,
      runAsynchronous: true,
      sharedUsers: this.state.sharedUsers,
      specialtyGapFilter: this.state.specialtyFilter.filterType,
      specialtyGroupCodes: this.props.specialtyGroupCodes,
    };
    await this.exportService.postExportAdequacyServicingProviders(servicingProviderModel);

  }

  // Attestation
  private generateExportAttestationServicingProvidersModel = async () => {
    const servicingProviderModel: ExportAttestationServicingProvidersModel = {
      comparedProjectRunConfigurationId: this.state.providerFilter.providerAvailabilityType === ExportProviderAvailabilityType.Changed ? this.props.comparedRunConfigurationId : null,
      customProjectId: this.props.projectId,
      eMailOnSuccess: this.state.emailOnSuccess,
      exportName: this.state.reportName,
      formatType: this.state.outputFormat,
      hsdFormat: this.state.hsdFormat, //&& this.state.outputFormat !== ExportFormatType.Excel, // hsd is not valid for Excel exports, no matter what...
      includeActiveRegionSets: this.state.regionSetFilter.includeActive,
      includeApprovedRegionSets: this.state.regionSetFilter.includeApproved,
      includeFacilitySpecialties: this.state.specialtyFilter.includeFacilitySpecialties,
      includePendingRegionSets: this.state.regionSetFilter.includePending,
      includePhysicianSpecialties: this.state.specialtyFilter.includePhysicianSpecialties,
      projectRunConfigurationId: this.props.runConfigurationId,
      projectURL: this.props.projectURL,
      providerIds: this.props.providerIds,
      providerNetworkType: this.state.providerFilter.filterType,
      regionSetGapFilter: this.state.regionSetFilter.filterType,
      regionSetIds: this.props.regionSetIds,
      runAsynchronous: true,
      specialtyGapFilter: this.state.specialtyFilter.filterType,
      specialtyGroupCodes: this.props.specialtyGroupCodes,
    };
    await this.exportService.postExportAttestationServicingProviders(servicingProviderModel);
  }

  // Composite
  private generateExportCompositeServicingProvidersModel = async () => {
    const servicingProviderModel: ExportCompositeServicingProvidersModel = {
      comparedProjectRunConfigurationId: this.state.providerFilter.providerAvailabilityType === ExportProviderAvailabilityType.Changed ? this.props.comparedRunConfigurationId : null,
      customProjectId: this.props.projectId,
      eMailOnSuccess: this.state.emailOnSuccess,
      exportName: this.state.reportName,
      formatType: this.state.outputFormat,
      hsdFormat: this.state.hsdFormat, //&& this.state.outputFormat !== ExportFormatType.Excel, // hsd is not valid for Excel exports, no matter what...
      includeActiveRegionSets: this.state.regionSetFilter.includeActive,
      includeApprovedRegionSets: this.state.regionSetFilter.includeApproved,
      includeFacilitySpecialties: this.state.specialtyFilter.includeFacilitySpecialties,
      includePendingRegionSets: this.state.regionSetFilter.includePending,
      includePhysicianSpecialties: this.state.specialtyFilter.includePhysicianSpecialties,
      projectRunConfigurationId: this.props.runConfigurationId,
      projectURL: this.props.projectURL,
      providerIds: this.props.providerIds,
      providerNetworkType: this.state.providerFilter.filterType,
      regionSetGapFilter: this.state.regionSetFilter.filterType,
      regionSetIds: this.props.regionSetIds,
      runAsynchronous: true,
      specialtyGapFilter: this.state.specialtyFilter.filterType,
      specialtyGroupCodes: this.props.specialtyGroupCodes,
    };
    await this.exportService.postExportCompositeServicingProviders(servicingProviderModel);
  }

  private handleEmailOnSuccessChange = (emailOnSuccess: boolean) => {
    this.setState({ emailOnSuccess, reportSettingsHasChanges: true, });
  }

  private handleReportNameChange = (reportName: string) => {
    this.setState({ reportName });
  }

  private updateSharedUsers = (sharedUsers: number[]) => {
    this.setState({ sharedUsers });
  }

  private handleSpecialtyIncludeChange = (selected: boolean, isPhysician: boolean) => {

    let physiciansSelected: boolean = this.state.specialtyFilter.includePhysicianSpecialties;
    let facilitiesSelected: boolean = this.state.specialtyFilter.includeFacilitySpecialties;

    if (isPhysician) {
      physiciansSelected = selected;
    } else {
      facilitiesSelected = selected;
    }

    this.setState((prevState) => ({
      ...prevState,
      reportSettingsHasChanges: true,
      specialtyFilter: {
        ...prevState.specialtyFilter,
        includeFacilitySpecialties: facilitiesSelected,
        includePhysicianSpecialties: physiciansSelected,
      },
    }));
  }

  private handleSpecialtyGapFilterChange = (filterType: ExportGapFilterType) => {
    this.setState((prevState) => ({
      ...prevState,
      reportSettingsHasChanges: true,
      specialtyFilter: {
        ...prevState.specialtyFilter,
        filterType,
      },
    }));
  }

  private handleSpecialtyAnalysisTypeChange = (networkAssessmentAnalysisType: NetworkAssessmentAnalysisType) => {
    this.setState((prevState) => ({
      ...prevState,
      reportSettingsHasChanges: true,
      specialtyFilter: {
        ...prevState.specialtyFilter,
        networkAssessmentAnalysisType,
      },
    }));
  }

  // Report Settings
  private stringifyStateReportSettings = (): string => {
    return helpers.stringifyStateReportSettings({
      appendNextClosestProviders: this.state.appendNextClosestProviders,
      emailOnSuccess: this.state.emailOnSuccess,
      hsdFormat: this.state.hsdFormat,
      loadingReportSettingsJSONStrings: this.state.loadingReportSettingsJSONStrings,
      networkProviderFilterType: this.state.networkProviderFilterType,
      outputFormat: this.state.outputFormat,
      providerComparisonTypes: this.state.providerComparisonTypes,
      providerFilter: this.state.providerFilter,
      regionSetFilter: this.state.regionSetFilter,
      specialtyFilter: this.state.specialtyFilter,
    } as ServicingProvidersPanelBodyState);
  }

  private buildReportSettingsModel = (isDefault: boolean, name: string): ReportSettingsModel => {
    const reportSettingsJson = this.stringifyStateReportSettings();
    return {
      isDefault,
      name,
      reportSettingsJson,
      type: this.reportSettingsType,
    } as ReportSettingsModel;
  }

  private get reportSettingsType() {
    switch (this.props.match.params.module) {
      case "accuracy":
        return ReportSettingsType.AccuracyServicingProviders;
      default:
        return ReportSettingsType.Unknown;
    }
  }

  private handleParseReportSettingsJsonToState = (reportSettingsJson: string) => {
    const settings = helpers.tryParseReportSettings(reportSettingsJson);
    if (settings) {
      this.setReportSettingsIsSelectionValid(true);
      this.setState((prevState) => ({
        ...prevState,
        ...settings,
      }));
    }
    else {
      const initialBodyState = this.getInitialBodyState();
      this.setReportSettingsIsSelectionValid(false);
      this.setState((prevState) => ({
        ...prevState,
        ...initialBodyState
      }));
    }
  }

  private setCurrentReportSettings = (currentReportSettings: ReportSettingsModel) => {
    this.setState((prevState) => ({
      ...prevState,
      currentReportSettings,
    }));
  }

  private setReportSettingsList = (rsList: ReportSettingsModel[]) => {
    const reportSettingsList = this.props.sortedReportSettings(rsList, "name");
    this.setState((prevState) => ({
      ...prevState,
      reportSettingsList,
    }))
  }

  private setReportSettingsHasChanges = (reportSettingsHasChanges: boolean) => {
    this.setState((prevState) => ({
      ...prevState,
      reportSettingsHasChanges,
    }))
  }

  private setReportSettingsHasInvalidChanges = (reportSettingsHasInvalidChanges: boolean) => {
    this.setState((prevState) => ({
      ...prevState,
      reportSettingsHasInvalidChanges,
    }))
  }

  private setReportSettingsIsSelectionValid = (isSelectionValid: boolean) => {
    this.setState((prevState) => ({
      ...prevState,
      isSelectionValid,
    }))
  }

  private setCreateReportSettingsNameInputDisabled = (createReportSettingsNameInputDisabled: boolean) => {
    this.setState((prevState) => ({
      ...prevState,
      createReportSettingsNameInputDisabled,
    }))
  }

  private setSaveClicked = (saveClicked: boolean) => {
    this.setState((prevState) => ({
      ...prevState,
      saveClicked,
    }))
  }
}
